/** @jsxImportSource @emotion/react */
import {
  Container,
  ContentContainer,
  ButtonContainer,
  TitleContainer,
  TitleSection,
  ListContainer,
  ItemContainer,
} from "./MinimumAttendeesSettingItemList.styles";
import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import EmptyAttendeesSettingItem from "../MinimumAttendeesSettingItem/EmptyMinimimAttendeesSettingItem";
import MinimumAttendeesSettingItem from "../MinimumAttendeesSettingItem/MinimumAttendeesSettingItem";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { ErrorType } from "../../types";

export type TTimeSlotMinAttendeesPolicy = {
  id: string;
  startTime?: string;
  endTime?: string;
  minAttendeeNum?: number;
  errorType?: ErrorType[];
};

const MinimumAttendeesSettingItemList = ({
  room,
  onChange,
}: {
  room: TMeetingRoom;
  onChange: (policies: TTimeSlotMinAttendeesPolicy[]) => void;
}) => {
  const [policies, setPolicies] = useState<TTimeSlotMinAttendeesPolicy[]>([]);

  const getRandomId = (length: number) => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
    let randomId = "";
    for (let i = 0; i < 10; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      randomId += characters.charAt(randomIndex);
    }

    return randomId;
  };

  const onAddTimeSlotMinAttendeesPolicy = () => {
    settingPolicyAfterCheck([
      ...policies,
      {
        id: getRandomId(10),
      },
    ]);
  };

  const findOverlappingTimeSlots = (policies: TTimeSlotMinAttendeesPolicy[]) => {
    return policies.filter((policy, index) =>
      policies.some(
        (otherPolicy, otherIndex) =>
          index !== otherIndex &&
          policy.startTime &&
          policy.endTime &&
          otherPolicy.startTime &&
          otherPolicy.endTime &&
          !(
            policy.endTime.slice(0, 5) <= otherPolicy.startTime.slice(0, 5) ||
            policy.startTime.slice(0, 5) >= otherPolicy.endTime.slice(0, 5)
          )
      )
    );
  };

  const onChangePolicy = (updateIdx: number, updatePolicy: TTimeSlotMinAttendeesPolicy) => {
    let updatePolicies = policies.map((policy, index) => (index === updateIdx ? updatePolicy : policy));
    settingPolicyAfterCheck(updatePolicies);
  };

  const onDeletePolicy = (deleteIdx: number) => {
    let updatePolicies = policies.filter((_, idx) => idx !== deleteIdx);
    settingPolicyAfterCheck(updatePolicies);
  };

  const settingPolicyAfterCheck = (updatePolicies: TTimeSlotMinAttendeesPolicy[]) => {
    // INFO: 제한 시간중복 체크
    const overLappingTimeSlots = findOverlappingTimeSlots(updatePolicies);
    if (overLappingTimeSlots.length > 0) {
      // 제한 시간 에러 추가
      updatePolicies = updatePolicies.map((policy) => ({
        ...policy,
        errorType: overLappingTimeSlots.some(
          (overlap) => overlap.startTime === policy.startTime && overlap.endTime === policy.endTime
        )
          ? [...(policy.errorType ?? []), ErrorType.TIME]
          : policy.errorType ?? [],
      }));
    } else {
      // 제한 시간 에러 제거
      updatePolicies = updatePolicies.map((policy) => ({
        ...policy,
        errorType: policy.errorType?.filter((error) => error !== ErrorType.TIME) ?? [],
      }));
    }
    setPolicies(updatePolicies);
  };

  useEffect(() => {
    onChange(policies);
  }, [policies]);

  useEffect(() => {
    const timeSlotMinAttendeesPolicy: TTimeSlotMinAttendeesPolicy[] = (room.attendeeLimits ?? []).reduce<
      TTimeSlotMinAttendeesPolicy[]
    >((acc, policy) => {
      if (policy.startTime && policy.endTime && policy.minAttendeeNum !== undefined) {
        acc.push({
          id: policy.id,
          startTime: policy.startTime,
          endTime: policy.endTime,
          minAttendeeNum: policy.minAttendeeNum,
        });
      }
      return acc;
    }, []);

    if (timeSlotMinAttendeesPolicy.length === 0) {
      onAddTimeSlotMinAttendeesPolicy();
    } else settingPolicyAfterCheck(timeSlotMinAttendeesPolicy);
  }, []);

  return (
    <Container>
      <ContentContainer>
        {policies.length === 0 ? (
          <EmptyAttendeesSettingItem />
        ) : (
          <ItemContainer>
            <TitleContainer>
              <TitleSection style={{ width: "263px" }}>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  인원 제한 시간
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
                  *
                </UI.Typography>
              </TitleSection>
              <TitleSection>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative)">
                  최소 참석 인원
                </UI.Typography>
                <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-primary-normal)">
                  *
                </UI.Typography>
              </TitleSection>
            </TitleContainer>
            <ListContainer>
              {policies.map((policy, idx) => {
                return (
                  <MinimumAttendeesSettingItem
                    key={`${policy.id}-${idx}`}
                    idx={idx}
                    timeSlotMinAttenancePolicyInfo={policy}
                    onDelete={onDeletePolicy}
                    onChange={onChangePolicy}
                    room={room}
                  />
                );
              })}
            </ListContainer>
            {policies.some((policy) => policy.errorType?.includes(ErrorType.TIME)) ? (
              <UI.Typography type="label02" fontStyle="regular" color="var(--alias-color-status-negative)">
                제한 시간은 중첩될 수 없습니다.
              </UI.Typography>
            ) : (
              <></>
            )}
            {policies.some((policy) => policy.errorType?.includes(ErrorType.ATTENDEE_NUM)) ? (
              <UI.Typography type="label02" fontStyle="regular" color="var(--alias-color-status-negative)">
                최소 참석 인원은 2인 이상, 회의실 수용 인원 범위 내에서 설정 가능합니다.
              </UI.Typography>
            ) : (
              <></>
            )}
          </ItemContainer>
        )}
      </ContentContainer>
      <ButtonContainer>
        <UI.Button
          type="solid"
          size="medium"
          primary={false}
          title="인원 제한 추가"
          prefixIcon="addCircle"
          showPrefixIcon
          onClick={onAddTimeSlotMinAttendeesPolicy}
        />
      </ButtonContainer>
    </Container>
  );
};

export default MinimumAttendeesSettingItemList;
